// cities.ts — Lays a habitable planet's pops out into coastal cities. The pop
// count (millions) is server-authoritative wire data (types.ts `Planet.pops`); the
// LAYOUT — how many cities, how big each is, and WHERE each sits — is derived here.
//
// Positions are read straight off the planet's RENDERED terrain geometry
// (terrain.ts) — at whatever LOD is showing — so cities always sit on THAT mesh's
// shoreline: each vertex carries the normalized elevation the surface shader
// classifies land/sea on (`aElevation`), and the unit direction matches the shader's
// object-space `sdir`. Reading the render mesh (rather than a fixed re-sample) avoids
// cross-LOD normalization drift — higher detail captures sharper peaks, widening the
// elevation span and shifting the coastline, so a fixed-detail point can fall in the
// sea once the focused mesh loads. The lights therefore show at every LOD; the layout
// re-derives when the LOD changes (cheap — the coarse meshes have few vertices, and
// the focused mesh is already built for rendering). Deterministic per (id, geometry).
import type { BufferAttribute, BufferGeometry } from 'three';
import type { Planet } from '../../types';
import { makeRng } from '../../galaxy/rng';
import { cityName } from '../../galaxy/names';
import { PLANET_PRESETS } from './presets';
import { hashSeed } from './terrain';
import { HABITABLE_KINDS } from './model';

export interface City {
  /** Unit direction in the planet's object space (matches the surface mesh frame). */
  dir: [number, number, number];
  /** Angular radius of the urban footprint, radians — scales with population. */
  angularRadius: number;
  /** Population in millions (1 pop = 1,000,000 people). */
  pops: number;
}

// Rank-size (Zipf-ish) exponent: city i (rank 1..n) gets weight 1/i^ZIPF, so one
// dominant capital tapers to a tail of small towns.
const ZIPF = 0.9;
// Coastal band: land just above sea level is the shoreline. COAST_MARGIN keeps the
// chosen vertex clear of the surface shader's fBm coastline warp (±0.04) so it
// always renders as land; COAST_WINDOW is how far inland still counts as coastal.
const COAST_MARGIN = 0.05;
const COAST_WINDOW = 0.14;
// Footprint angular radius from city population: r = BASE + K*sqrt(pops), clamped.
// sqrt so the lit AREA grows ~linearly with population; the cap keeps a megacity
// from swallowing the disc.
const CITY_BASE_R = 0.02;
const CITY_R_K = 0.0014;
const CITY_MAX_R = 0.16;

// Climate inputs (the same the sidebar/shader use) needed to place cities only where
// the local surface temperature is livable — see planetCities.
export interface CityClimate {
  meanTempC: number;
  dayTempC: number;
  nightTempC: number;
  /** Axial tilt 0..1 (flattens the equator→pole gradient). */
  obliquity: number;
  /** Tidally locked → skip the latitude climate filter (habitable ring is the terminator). */
  tidallyLocked: boolean;
}

// Per-latitude annual-mean surface temperature (degC), mirroring surfaceTempC's
// rotator branch in surfaceShader.ts so placement agrees with what the shader paints
// (warm equator, cold poles; axial tilt flattens the contrast).
function latitudeTempC(latY: number, c: CityClimate): number {
  const lat = Math.abs(latY);
  const wInsol = Math.cos(lat * (Math.PI / 2)) * (1 - 0.5 * c.obliquity) + 0.5 * c.obliquity;
  const spread = Math.max(45, Math.min(100, (c.dayTempC - c.nightTempC) * 0.5 + 50));
  return c.meanTempC + spread * (wInsol - 0.55);
}

// Climate suitability 0..1 for a settlement: livable temperate land, falling to 0 on
// frozen (the shader frosts below ~8°C) or scorched (above ~40°C) ground.
function climateSuit(latY: number, c: CityClimate): number {
  const t = latitudeTempC(latY, c);
  return smoothstep01(-6, 10, t) * (1 - smoothstep01(36, 56, t));
}

function smoothstep01(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

// Ecumenopolis ramp: as a world grows past the point where new cities stop helping,
// the land between them paves over. urbanization ramps a global urban-density floor
// 0→1 from ECUMENOPOLIS_LO to a fully planet-spanning city (Coruscant/Trantor) at
// ECUMENOPOLIS_HI. Earth-like worlds (~10k pops) sit at 0 (discrete cities); only the
// most populous worlds merge, the very densest into a full planet-city. The band is
// compressed to the upper range the generator actually produces (matching farstars'
// other deliberately-compressed scales), so megaworlds are visible on a fresh galaxy
// rather than only after late-game pop growth. Drives uCityUrban in the surface shader.
const ECUMENOPOLIS_LO = 12_000;
const ECUMENOPOLIS_HI = 30_000;

export function urbanization(pops: number): number {
  if (pops <= ECUMENOPOLIS_LO) {
    return 0;
  }
  const t = (pops - ECUMENOPOLIS_LO) / (ECUMENOPOLIS_HI - ECUMENOPOLIS_LO);
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c); // smoothstep
}

/** Number of cities for a population: one per ~1000 pops, at least one, capped 50. */
export function cityCountForPops(pops: number): number {
  if (pops <= 0) {
    return 0;
  }
  return Math.min(50, Math.max(1, Math.round(pops / 1000)));
}

export interface CityInfo {
  name: string;
  /** Population in millions (1 pop = 1,000,000 people). */
  pops: number;
}

/**
 * The planet's cities as name + population, largest first — for the planet sheet.
 * No geometry needed (positions are derived separately by planetCities), so the
 * sidebar can list a world's cities whether or not it's the focused/rendered one.
 * Deterministic per planet.
 */
export function cityList(planet: Planet): CityInfo[] {
  const pops = cityPopulations(planet.pops);
  if (pops.length === 0) {
    return [];
  }
  const rng = makeRng(hashSeed(planet.id) ^ 0x5bd1e995);
  return pops.map((p) => ({ name: cityName(rng), pops: p }));
}

/** Per-city populations (millions), largest first, summing to ~pops (rank-size). */
export function cityPopulations(pops: number): number[] {
  const n = cityCountForPops(pops);
  if (n === 0) {
    return [];
  }
  const weights: number[] = [];
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const w = 1 / Math.pow(i + 1, ZIPF);
    weights.push(w);
    sum += w;
  }
  return weights.map((w) => Math.max(1, Math.round((pops * w) / sum)));
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

// mulberry32 — deterministic PRNG so the spread-selection (and thus the city
// layout) is stable for a given planet across reloads/clients.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Greedy farthest-point sampling: pick a seeded start, then repeatedly add the
// candidate maximizing its minimum angular distance to the already-chosen set, so
// the cities spread around the globe instead of clumping on one coast. Distance is
// 1 - dot (0 = same point, 2 = antipodal) on the unit directions.
function spreadSelect(pts: ReadonlyArray<[number, number, number]>, k: number, rng: () => number): number[] {
  const n = pts.length;
  if (n <= k) {
    return pts.map((_, i) => i);
  }
  const taken = new Array<boolean>(n).fill(false);
  const minD = new Float64Array(n).fill(Infinity);
  const chosen: number[] = [];
  let cur = Math.floor(rng() * n);
  taken[cur] = true;
  chosen.push(cur);
  for (let step = 1; step < k; step++) {
    const c = pts[cur];
    let far = -1;
    let farD = -1;
    for (let i = 0; i < n; i++) {
      if (taken[i]) {
        continue;
      }
      const p = pts[i];
      const d = 1 - (p[0] * c[0] + p[1] * c[1] + p[2] * c[2]);
      if (d < minD[i]) {
        minD[i] = d;
      }
      if (minD[i] > farD) {
        farD = minD[i];
        far = i;
      }
    }
    if (far < 0) {
      break;
    }
    taken[far] = true;
    chosen.push(far);
    cur = far;
  }
  return chosen;
}

/**
 * A habitable planet's coastal cities (positions, sizes, populations), derived from
 * its population and seated on the shoreline of the supplied terrain `geometry`
 * (terrain.ts) — pass whatever LOD mesh is being rendered, and the cities track that
 * mesh's coast. Returns [] for uninhabited/non-habitable worlds.
 */
export function planetCities(planet: Planet, geometry: BufferGeometry, climate: CityClimate): City[] {
  const n = cityCountForPops(planet.pops);
  if (n === 0 || !HABITABLE_KINDS.has(planet.kind)) {
    return [];
  }
  const pos = geometry.getAttribute('position') as BufferAttribute | undefined;
  const elev = geometry.getAttribute('aElevation') as BufferAttribute | undefined;
  if (!pos || !elev) {
    return [];
  }
  const seaLevel = PLANET_PRESETS[planet.kind].ramp.seaLevel01;
  const count = pos.count;

  // Coastal land: the shoreline ring of vertices just above sea level.
  const lo = seaLevel + COAST_MARGIN;
  const hi = lo + COAST_WINDOW;
  let pool: number[] = [];
  for (let i = 0; i < count; i++) {
    const h = elev.getX(i);
    if (h >= lo && h <= hi) {
      pool.push(i);
    }
  }
  // Land-poor fallbacks: widen to all dry land, then to the highest points so even
  // an almost-all-ocean world still seats its (few) cities on its best land.
  if (pool.length < n) {
    pool = [];
    for (let i = 0; i < count; i++) {
      if (elev.getX(i) >= lo) {
        pool.push(i);
      }
    }
  }
  if (pool.length < n) {
    pool = Array.from({ length: count }, (_, i) => i)
      .sort((a, b) => elev.getX(b) - elev.getX(a))
      .slice(0, Math.max(n * 8, 64));
  }

  // Climate filter: keep only coastal land in a livable temperate band (the shader
  // paints the rest as ice cap / scorched, where it also darkens the lights), so
  // cities don't end up on a frozen pole or a baking equator. Latitude = the vertex's
  // normalized y. Relax, then drop the filter, if a marginal world has too little
  // suitable coast. Skipped for tidally-locked worlds (no latitude bands).
  const latOf = (i: number): number => {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    return y / (Math.hypot(x, y, z) || 1);
  };
  if (!climate.tidallyLocked) {
    const suitable = pool.filter((i) => climateSuit(latOf(i), climate) > 0.35);
    if (suitable.length >= n) {
      pool = suitable;
    } else {
      const relaxed = pool.filter((i) => climateSuit(latOf(i), climate) > 0.12);
      if (relaxed.length >= n) {
        pool = relaxed;
      }
    }
  }

  // Object-space unit directions: normalize the radially-displaced vertex position
  // (radial displacement preserves direction), matching the shader's `sdir`.
  const dirs: [number, number, number][] = pool.map((i) => {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const len = Math.hypot(x, y, z) || 1;
    return [x / len, y / len, z / len];
  });

  const rng = mulberry32(hashSeed(planet.id) ^ 0x9e3779b9);
  const chosen = spreadSelect(dirs, n, rng);
  const pops = cityPopulations(planet.pops);
  return chosen.map((ci, rank) => {
    const cityPops = pops[rank] ?? pops[pops.length - 1] ?? 1;
    const angularRadius = clamp(CITY_BASE_R + CITY_R_K * Math.sqrt(cityPops), CITY_BASE_R, CITY_MAX_R);
    return { dir: dirs[ci], angularRadius, pops: cityPops };
  });
}
